import React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import Button, { ButtonType } from 'components/Button';
import AIThinkingLoader from 'components/FinancialCheckup/AICoach/AIThinkingLoader';
import { getAIChat } from 'selectors/aiChat';
import { setActiveWorkflow, toggleAIChat, WorkflowTypes, incrementConversationCount } from 'handlers/aiChat';
import { getGoalOptimizer } from 'selectors/goalOptimizer';
import { setHasAcceptedDisclosure } from 'handlers/goalOptimizer';
import { recommendGoal } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';

import styles from './WelcomeMessage.module.scss';

interface WelcomeMessageProps {
  firstName?: string;
  showLoader?: boolean;
}

const WelcomeMessage = ({ firstName, showLoader = false }: WelcomeMessageProps) => {
  const dispatch = useDispatch();
  const { application } = useSelector(getApplicationData);
  const { recommendation, isLoading, hasAcceptedDisclosure } = useSelector(getGoalOptimizer);
  const { activeWorkflow, conversationCount } = useSelector(getAIChat);
  const { plaidConnected } = useSelector(getLoanGoal);
  const trackOptionSelected = (option: string) => {
    const newCount = conversationCount + 1;
    dispatch(incrementConversationCount());
    analytics.track('AI Coach Option Selected', { option, conversationCount: newCount });
  };

  const showOneTimeDisclosure = !recommendation && !isLoading && !hasAcceptedDisclosure;
  const showPlaidAfterDisclosure = !recommendation && !isLoading && hasAcceptedDisclosure && !plaidConnected;

  return (
    <>
      Hi{firstName ? ` ${firstName}` : ''}! 👋 I'm your AI assistant. I can help you fast track your goals. <br />
      <br />
      {showOneTimeDisclosure && (
        <div className={styles.oneTimeDisclosure}>
          <p>
            Before we dive in, here's something to know: I'm an AI designed to help you set and track financial goals.
            My suggestions are meant to guide you, but it's not professional financial, legal, or investment advice.
            <br />
            <br />
          </p>
          <div className={styles.buttonContainer}>
            <Button
              type={ButtonType.ChatOptionButton}
              onClick={() => {
                trackOptionSelected('Sounds good!');
                dispatch(setHasAcceptedDisclosure(true));
                if (plaidConnected) {
                  analytics.track('Recommended Action');
                  dispatch(recommendGoal({ applicationId: application?.id! }));
                }
              }}
              loaderSize={50}
            >
              Sounds good!
            </Button>
          </div>
        </div>
      )}
      {showPlaidAfterDisclosure && (
        <div className={styles.welcomeMessageContainer}>
          To get started and unlock your personalized financial plan, please connect your bank account through Plaid.
          {!activeWorkflow?.isLoading && (
            <div className={styles.buttonContainer}>
              <Button
                type={ButtonType.ChatOptionButton}
                onClick={() => {
                  trackOptionSelected('Connect my bank account');
                  dispatch(
                    setActiveWorkflow({
                      success: false,
                      type: WorkflowTypes.Plaid,
                      isLoading: true,
                      openPlaid: true,
                      fullScreen: false,
                    }),
                  );
                }}
                isLoading={activeWorkflow?.isLoading && activeWorkflow?.type === WorkflowTypes.Plaid}
                loaderSize={50}
              >
                Connect my bank account
              </Button>
              <Button
                type={ButtonType.ChatOptionButton}
                onClick={() => {
                  trackOptionSelected('Skip for now');
                  dispatch(toggleAIChat());
                }}
              >
                Skip for now
              </Button>
            </div>
          )}
        </div>
      )}
      {(showLoader || (activeWorkflow?.isLoading && showPlaidAfterDisclosure)) && <AIThinkingLoader />}
    </>
  );
};

export default WelcomeMessage;
